// ════════════════════════════════════════════════════════════
// Compras — pedidos a proveedor, recepciones y proveedores
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Grid, Card, CardContent, Chip, Alert, Button,
  TextField, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, CircularProgress, Divider, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  getPedidosCompra, getPedidoCompra, cambiarEstadoPedidoCompra, registrarRecepcion,
  descargarArchivoFacturaProveedor, getCategoriasCompra, descargarPedidoCompraPDF
} from '../api';
import ProveedoresCompras from '../components/ProveedoresCompras';
import RegistrarFacturaProveedor from '../components/RegistrarFacturaProveedor';
import PedidoCompraEditor from '../components/PedidoCompraEditor';

export const ESTADOS_PC: any = {
  borrador:         { label: 'Borrador',         color: 'default' },
  enviado:          { label: 'Emitido',          color: 'info' },
  recibido_parcial: { label: 'Recibido parcial', color: 'warning' },
  recibido:         { label: 'Recibido',         color: 'success' },
  facturado:        { label: 'Facturado',        color: 'secondary' },
  anulado:          { label: 'Anulado',          color: 'error' },
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function Compras() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
        🛒 Compras
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab label="📦 Pedidos a proveedor" />
        <Tab label="🏭 Proveedores" />
      </Tabs>
      {tab === 0 && <PedidosCompra />}
      {tab === 1 && <ProveedoresCompras />}
    </Box>
  );
}

function PedidosCompra() {
  const [pedidos, setPedidos] = useState<any[]>([]);
  const [categorias, setCategorias] = useState<any[]>([]);
  const [filtro, setFiltro] = useState('');
  const [tipo, setTipo] = useState('');
  const [search, setSearch] = useState('');
  const [cargando, setCargando] = useState(true);
  const [detalle, setDetalle] = useState<any>(null);
  const [recepcion, setRecepcion] = useState<any>(null);
  const [procesando, setProcesando] = useState(false);
  const [facturaReg, setFacturaReg] = useState<any>(null);
  const [editor, setEditor] = useState<any>(null);   // 'nuevo' o el pedido a editar

  useEffect(() => { getCategoriasCompra().then(setCategorias).catch(() => {}); }, []);

  const cargar = () => {
    setCargando(true);
    getPedidosCompra({ estado: filtro, search, tipo })
      .then(setPedidos).catch(() => setPedidos([]))
      .finally(() => setCargando(false));
  };
  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, search, tipo]);

  const nombreTipo = (c: string) => categorias.find((x: any) => x.clave === c)?.nombre || c || '';

  const abrir = async (id: number) => {
    try { setDetalle(await getPedidoCompra(id)); } catch { alert('No se pudo cargar el pedido'); }
  };
  const refrescar = async () => {
    cargar();
    if (detalle) setDetalle(await getPedidoCompra(detalle.id));
  };

  const cambiarEstado = async (estado: string, pregunta: string) => {
    if (!window.confirm(pregunta)) return;
    setProcesando(true);
    try { await cambiarEstadoPedidoCompra(detalle.id, estado); await refrescar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error al cambiar el estado'); }
    finally { setProcesando(false); }
  };

  const abrirRecepcion = () => setRecepcion({
    fecha: new Date().toISOString().slice(0, 10), albaran_proveedor: '', notas: '',
    lineas: detalle.lineas
      .map((l: any) => ({ ...l, pendiente: Math.max(0, (l.cantidad || 0) - (l.cantidad_recibida || 0)) }))
      .filter((l: any) => l.pendiente > 0)
      .map((l: any) => ({ ...l, recibir: l.pendiente })),
  });

  const confirmarRecepcion = async () => {
    const lineas = recepcion.lineas
      .filter((l: any) => parseFloat(l.recibir) > 0)
      .map((l: any) => ({ pedido_linea_id: l.id, cantidad: parseFloat(l.recibir) }));
    if (!lineas.length) { alert('Indica al menos una cantidad recibida'); return; }
    setProcesando(true);
    try {
      const r = await registrarRecepcion(detalle.id, {
        fecha: recepcion.fecha, albaran_proveedor: recepcion.albaran_proveedor,
        notas: recepcion.notas, lineas,
      });
      setRecepcion(null);
      await refrescar();
      alert(r.completo ? '✅ Pedido recibido completo. El material ya está en stock.'
                       : '✅ Recepción parcial registrada. Queda material pendiente.');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al registrar la recepción');
    } finally {
      setProcesando(false);
    }
  };

  const tras_guardar_pedido = async (r: any) => {
    cargar();
    if (detalle) {
      setDetalle(await getPedidoCompra(detalle.id));
    } else if (r?.numero) {
      alert(r.emitido
        ? `✅ Pedido ${r.numero} emitido. Ya aparece en Administración → Facturas a pagar.`
        : `✅ Pedido ${r.numero} guardado como borrador.`);
    }
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField fullWidth size="small" label="Buscar (nº de pedido o proveedor)"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo</InputLabel>
            <Select value={tipo} label="Tipo" onChange={(e) => setTipo(String(e.target.value))}>
              <MenuItem value="">Todos</MenuItem>
              {categorias.map((c: any) => <MenuItem key={c.clave} value={c.clave}>{c.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Estado</InputLabel>
            <Select value={filtro} label="Estado" onChange={(e) => setFiltro(String(e.target.value))}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(ESTADOS_PC).map(([k, v]: any) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3} sx={{ textAlign: { md: 'right' } }}>
          <Button variant="contained" startIcon={<AddIcon />} fullWidth
            onClick={() => { setDetalle(null); setEditor('nuevo'); }} sx={{ bgcolor: '#4f46e5' }}>
            Nuevo pedido
          </Button>
        </Grid>
      </Grid>

      {cargando ? <CircularProgress /> : pedidos.length === 0 ? (
        <Alert severity="info">
          No hay pedidos con esos criterios. Pulsa «Nuevo pedido» para crear uno; también se generan
          solos al aceptar ofertas del almacén con material que falta.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {pedidos.map((p: any) => {
            const est = ESTADOS_PC[p.estado] || { label: p.estado, color: 'default' };
            const pct = p.uds_pedidas > 0 ? Math.round(p.uds_recibidas / p.uds_pedidas * 100) : 0;
            return (
              <Grid item xs={12} sm={6} lg={4} key={p.id}>
                <Card onClick={() => abrir(p.id)}
                  sx={{ borderRadius: 3, cursor: 'pointer', height: '100%', '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>{p.numero}</Typography>
                      <Chip size="small" label={est.label} color={est.color} />
                    </Box>
                    <Typography variant="body2"
                      sx={{ color: p.proveedor_id ? 'inherit' : 'warning.main', fontWeight: p.proveedor_id ? 400 : 700 }}>
                      🏭 {p.proveedor_nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      📅 {p.fecha} · {p.n_lineas} línea(s) · {eur(p.total)}
                    </Typography>
                    <Chip size="small" variant="outlined" label={nombreTipo(p.tipo_compra)} sx={{ mt: 1 }} />
                    {['enviado', 'recibido_parcial'].includes(p.estado) && p.tipo_compra !== 'servicios'
                      && p.tipo_compra !== 'transporte' && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress variant="determinate" value={pct} sx={{ borderRadius: 1 }} />
                        <Typography variant="caption" color="text.secondary">Recibido {pct}%</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* DETALLE */}
      <Dialog open={!!detalle} maxWidth="md" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setDetalle(null); }}>
        {detalle && (() => {
          const est = ESTADOS_PC[detalle.estado] || { label: detalle.estado, color: 'default' };
          return (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <span>📦 Pedido {detalle.numero}</span>
                  <Chip label={est.label} color={est.color} />
                </Box>
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2">🏭 <b>{detalle.proveedor_nombre}</b> · {nombreTipo(detalle.tipo_compra)}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Fecha {detalle.fecha}
                  {detalle.fecha_prevista ? ` · Prevista ${detalle.fecha_prevista}` : ''} · Total {eur(detalle.total)}
                </Typography>
                {!detalle.proveedor_id && detalle.estado === 'borrador' && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Este pedido no tiene proveedor. Pulsa «Editar» para asignarlo antes de emitirlo.
                  </Alert>
                )}
                {detalle.factura && (
                  <Alert severity={detalle.factura.estado === 'pendiente_recibir' ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    {detalle.factura.estado === 'pendiente_recibir'
                      ? 'Factura del proveedor pendiente de recibir.'
                      : `Factura ${detalle.factura.numero_proveedor} registrada${detalle.factura.estado === 'pagada' ? ' y pagada' : ''}.`}
                  </Alert>
                )}
                {detalle.observaciones && (
                  <Typography variant="caption" display="block" sx={{ mb: 2 }}>📝 {detalle.observaciones}</Typography>
                )}

                <TableContainer sx={{ overflowX: 'auto', mb: 2 }}>
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Referencia</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Pedido</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Recibido</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalle.lineas.map((l: any) => (
                        <TableRow key={l.id}>
                          <TableCell>{l.referencia || '—'}</TableCell>
                          <TableCell>{l.descripcion}</TableCell>
                          <TableCell align="right">{l.cantidad}</TableCell>
                          <TableCell align="right"
                            sx={{ color: l.cantidad_recibida >= l.cantidad ? 'success.main' : 'inherit', fontWeight: 600 }}>
                            {l.cantidad_recibida || 0}
                          </TableCell>
                          <TableCell align="right">{eur(l.base_linea)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {detalle.pedidos_cliente?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Para pedidos de cliente</Typography>
                    {detalle.pedidos_cliente.map((pc: any) => (
                      <Typography key={pc.id} variant="body2">🚚 <b>{pc.numero}</b> — {pc.cliente_nombre}</Typography>
                    ))}
                  </Box>
                )}

                {detalle.recepciones?.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Recepciones</Typography>
                    {detalle.recepciones.map((r: any) => (
                      <Box key={r.id} sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {r.fecha}{r.albaran_proveedor ? ` · Albarán ${r.albaran_proveedor}` : ''}
                          {r.recibido_por_nombre ? ` · ${r.recibido_por_nombre}` : ''}
                        </Typography>
                        {r.lineas.map((x: any, i: number) => (
                          <Typography key={i} variant="caption" display="block" color="text.secondary">
                            {x.cantidad} × {x.referencia ? `${x.referencia} — ` : ''}{x.descripcion}
                          </Typography>
                        ))}
                      </Box>
                    ))}
                  </>
                )}
              </DialogContent>
              <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                {['borrador', 'enviado'].includes(detalle.estado) && (
                  <Button color="error" disabled={procesando}
                    onClick={() => cambiarEstado('anulado', `¿Anular el pedido ${detalle.numero}?`)}>
                    Anular
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={() => setDetalle(null)}>Cerrar</Button>
                <Button onClick={() => descargarPedidoCompraPDF(detalle.id, detalle.numero)}>
                  📄 PDF del pedido
                </Button>
                {detalle.factura?.archivo && (
                  <Button onClick={() => descargarArchivoFacturaProveedor(detalle.factura.id, detalle.factura.archivo_nombre)}>
                    📎 Ver factura
                  </Button>
                )}
                {detalle.factura && detalle.factura.estado !== 'pagada' && (
                  <Button variant="outlined" onClick={() => setFacturaReg(detalle.factura)}>
                    🧾 {detalle.factura.estado === 'pendiente_recibir' ? 'Registrar factura' : 'Corregir factura'}
                  </Button>
                )}
                {detalle.estado === 'borrador' && (
                  <Button variant="outlined" disabled={procesando} onClick={() => setEditor(detalle)}>
                    ✏️ Editar
                  </Button>
                )}
                {detalle.estado === 'borrador' && (
                  <Button variant="contained" disabled={procesando} sx={{ bgcolor: '#4f46e5' }}
                    onClick={() => cambiarEstado('enviado',
                      `¿Emitir el pedido ${detalle.numero} a ${detalle.proveedor_nombre}?\n\nAparecerá en Administración como factura a pagar.`)}>
                    📤 Emitir pedido
                  </Button>
                )}
                {['enviado', 'recibido_parcial'].includes(detalle.estado) && (
                  <Button variant="contained" disabled={procesando} onClick={abrirRecepcion}
                    sx={{ bgcolor: '#22c55e' }}>
                    📥 Registrar recepción
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <PedidoCompraEditor open={!!editor} pedido={editor === 'nuevo' ? null : editor}
        onClose={() => setEditor(null)} onGuardado={tras_guardar_pedido} />

      <RegistrarFacturaProveedor open={!!facturaReg} factura={facturaReg}
        onClose={() => setFacturaReg(null)} onGuardada={refrescar} />

      {/* RECEPCIÓN */}
      <Dialog open={!!recepcion} maxWidth="md" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setRecepcion(null); }}>
        <DialogTitle>📥 Registrar mercancía recibida</DialogTitle>
        <DialogContent>
          {recepcion && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth size="small" type="date" label="Fecha" InputLabelProps={{ shrink: true }}
                    value={recepcion.fecha} onChange={(e) => setRecepcion({ ...recepcion, fecha: e.target.value })} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Nº albarán del proveedor"
                    value={recepcion.albaran_proveedor}
                    onChange={(e) => setRecepcion({ ...recepcion, albaran_proveedor: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField fullWidth size="small" label="Notas (daños, faltas...)"
                    value={recepcion.notas} onChange={(e) => setRecepcion({ ...recepcion, notas: e.target.value })} />
                </Grid>
              </Grid>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Artículo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Pendiente</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130 }}>Recibido ahora</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recepcion.lineas.map((l: any, i: number) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.referencia ? `${l.referencia} — ` : ''}{l.descripcion}</TableCell>
                      <TableCell align="right">{l.pendiente}</TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={l.recibir}
                          inputProps={{ min: 0, max: l.pendiente }}
                          onChange={(e) => {
                            const lineas = [...recepcion.lineas];
                            lineas[i] = { ...l, recibir: e.target.value };
                            setRecepcion({ ...recepcion, lineas });
                          }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecepcion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmarRecepcion} disabled={procesando} sx={{ bgcolor: '#22c55e' }}>
            Confirmar recepción
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
